import React, { useState, useEffect } from 'react';
import Banner from '../components/common/Banner';
import AdmissionEnquiryForm from '../components/common/AdmissionEnquiryForm';
import { apiService } from '../services/api';
import { getImageUrl } from '../utils/imageUtils';

const AdmissionPage = () => {
  const [activeSection, setActiveSection] = useState('process');
  const [admissionsData, setAdmissionsData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isEnquiryFormOpen, setIsEnquiryFormOpen] = useState(false);

  useEffect(() => {
    fetchAdmissionsData();
  }, []);

  const fetchAdmissionsData = async () => {
    try {
      setLoading(true);
      const response = await apiService.getAdmissions();
      if (response.success) {
        setAdmissionsData(response.data);
      } else {
        throw new Error('Failed to load admissions data');
      }
    } catch (error) {
      console.error('Error fetching admissions data:', error);
      setError('Failed to load page content');
    } finally {
      setLoading(false);
    }
  };

  const renderProcessSection = () => {
    if (!admissionsData) return null;
    
    return (
      <div className="space-y-8">
        <div className="mb-8">
          <h2 className="text-2xl sm:text-3xl font-sans font-bold text-slate mb-3 sm:mb-4">Admission Process</h2>
          <p className="text-gray-500">Step-by-step guide for admission to B.Tech and Polytechnic programs.</p>
        </div>

        <div className="grid sm:grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6 lg:gap-8">
          {/* B.Tech Admission */}
          <div className="bg-white p-4 sm:p-6 rounded-xl border border-border_gray">
            <h3 className="text-lg sm:text-xl font-bold text-crimson mb-4 sm:mb-6 flex items-center">
              <i className="fas fa-graduation-cap mr-2"></i>
              B.Tech Admission Process
            </h3>
            
            <div className="space-y-4">
              <div className="bg-surface p-4 rounded-lg border border-gray-200">
                <h4 className="font-semibold text-slate mb-2">Eligibility Criteria</h4>
                <ul className="text-sm sm:text-base text-gray-600 space-y-1">
                  {admissionsData.btechEligibilityCriteria?.map((criterion, index) => (
                    <li key={index}>• {criterion}</li>
                  ))}
                </ul>
              </div>
              
              <div className="bg-surface p-4 rounded-lg border border-gray-200">
                <h4 className="font-semibold text-slate mb-2">Application Process</h4>
                <ol className="text-sm sm:text-base text-gray-600 space-y-1">
                  {admissionsData.btechApplicationProcess?.map((step, index) => (
                    <li key={index}>{index + 1}. {step}</li>
                  ))}
                </ol>
              </div>

              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <h4 className="font-semibold text-yellow-800 mb-2">Lateral Entry</h4>
                <p className="text-yellow-700 text-sm">
                  {admissionsData.btechFeeStructureNote}
                </p>
              </div>
            </div>
          </div>

          {/* Polytechnic Admission */}
          <div className="bg-white p-4 sm:p-6 rounded-xl border border-border_gray">
            <h3 className="text-lg sm:text-xl font-bold text-crimson mb-4 sm:mb-6 flex items-center">
              <i className="fas fa-tools mr-2"></i>
              Polytechnic Admission Process
            </h3>
            
            <div className="space-y-4">
              <div className="bg-surface p-4 rounded-lg border border-gray-200">
                <h4 className="font-semibold text-slate mb-2">Eligibility Criteria</h4>
                <ul className="text-sm sm:text-base text-gray-600 space-y-1">
                  {admissionsData.polytechnicEligibilityCriteria?.map((criterion, index) => (
                    <li key={index}>• {criterion}</li>
                  ))}
                </ul>
              </div>
              
              <div className="bg-surface p-4 rounded-lg border border-gray-200">
                <h4 className="font-semibold text-slate mb-2">Application Process</h4>
                <ol className="text-sm sm:text-base text-gray-600 space-y-1">
                  {admissionsData.polytechnicApplicationProcess?.map((step, index) => (
                    <li key={index}>{index + 1}. {step}</li>
                  ))}
                </ol>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="font-semibold text-blue-800 mb-2">Lateral Entry</h4>
                <p className="text-blue-700 text-sm">
                  {admissionsData.polytechnicLateralEntryNote}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderPaymentSection = () => {
    if (!admissionsData) return null;
    
    return (
      <div className="space-y-8">
        <div className="mb-8">
          <h2 className="text-2xl sm:text-3xl font-sans font-bold text-slate mb-3 sm:mb-4">Payment Procedure</h2>
          <p className="text-gray-500">Secure payment options and banking details for fee submission.</p>
        </div>

        <div className="grid sm:grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6 lg:gap-8">
          {/* Payment Instructions */}
          <div className="bg-white p-4 sm:p-6 rounded-xl border border-border_gray">
            <h3 className="text-lg sm:text-xl font-bold text-crimson mb-4 sm:mb-6">Payment Instructions</h3>
            
            <div className="space-y-4">
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <h4 className="font-semibold text-green-800 mb-2 flex items-center">
                  <i className="fas fa-shield-alt mr-2"></i>
                  Secure Payment Options
                </h4>
                <ul className="text-green-700 text-sm space-y-1">
                  {admissionsData.paymentOptions?.map((option, index) => (
                    <li key={index}>• {option}</li>
                  ))}
                </ul>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="font-semibold text-blue-800 mb-2">Payment Guidelines</h4>
                <ul className="text-blue-700 text-sm space-y-1">
                  {admissionsData.paymentGuidelines?.map((guideline, index) => (
                    <li key={index}>• {guideline}</li>
                  ))}
                </ul>
              </div>

              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <h4 className="font-semibold text-yellow-800 mb-2">Important Notes</h4>
                <ul className="text-yellow-700 text-sm space-y-1">
                  {admissionsData.paymentImportantNotes?.map((note, index) => (
                    <li key={index}>• {note}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>

          {/* Bank Details */}
          <div className="bg-white p-4 sm:p-6 rounded-xl border border-border_gray">
            <h3 className="text-lg sm:text-xl font-bold text-crimson mb-4 sm:mb-6">Bank Details</h3>
            
            <div className="space-y-6">
              <div className="bg-slate-50 p-4 rounded-lg border border-gray-200">
                <h4 className="font-semibold text-slate mb-4">For Fee Payment Transfer</h4>
                <div className="space-y-3 text-sm">
                  <div className="flex justify-between">
                    <span className="font-medium text-gray-600">Account Name:</span>
                    <span className="font-semibold">{admissionsData.bankAccountName}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="font-medium text-gray-600">Bank Name:</span>
                    <span className="font-semibold">{admissionsData.bankName}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="font-medium text-gray-600">Account Number:</span>
                    <span className="font-semibold">{admissionsData.bankAccountNumber}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="font-medium text-gray-600">IFSC Code:</span>
                    <span className="font-semibold">{admissionsData.bankIFSCCode}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="font-medium text-gray-600">Branch:</span>
                    <span className="font-semibold">{admissionsData.bankBranch}</span>
                  </div>
                </div>
              </div>


              {admissionsData.bankImportantNote && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <h4 className="font-semibold text-red-800 mb-2 flex items-center">
                    <i className="fas fa-exclamation-circle mr-2"></i>
                    Important Note
                  </h4>
                  <p className="text-red-700 text-sm">{admissionsData.bankImportantNote}</p>
                </div>
              )}

              {admissionsData.upiQrCode && (
                <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                  <h4 className="font-semibold text-purple-800 mb-3 flex items-center justify-center">
                    <i className="fas fa-qrcode mr-2"></i>
                    UPI Payment QR Code
                  </h4>
                  <div className="flex justify-center">
                    <img
                      src={admissionsData.upiQrCode.startsWith('http') ? admissionsData.upiQrCode : getImageUrl(admissionsData.upiQrCode)}
                      alt="UPI QR Code for Payment"
                      className="w-64 h-64 object-contain rounded-lg shadow-md"
                    />
                  </div>
                  <p className="text-purple-700 text-sm text-center mt-3">
                    Scan this QR code to make payment via UPI
                  </p>
                </div>
              )}

            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderDocumentsSection = () => {
    if (!admissionsData) return null;
    
    return (
      <div className="space-y-8">
        <div className="mb-8">
          <h2 className="text-2xl sm:text-3xl font-sans font-bold text-slate mb-3 sm:mb-4">Document Checklist</h2>
          <p className="text-gray-500">Required documents for admission process and verification.</p>
        </div>

        <div className="bg-white p-4 sm:p-6 md:p-8 rounded-xl border border-border_gray">
          <h3 className="text-lg sm:text-xl font-bold text-crimson mb-4 sm:mb-6">Required Documents</h3>
          
          <div className="grid sm:grid-cols-1 md:grid-cols-2 gap-x-4 sm:gap-x-8 gap-y-4">
            {admissionsData.requiredDocuments?.map((document, index) => (
              <div key={index} className="flex items-start space-x-3">
                <div className="bg-crimson text-white w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold mt-0.5 flex-shrink-0">
                  {index + 1}
                </div>
                <span className="text-gray-700">{document}</span>
              </div>
            ))}
          </div>

          <div className="mt-8 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <h4 className="font-semibold text-blue-800 mb-2 flex items-center">
              <i className="fas fa-info-circle mr-2"></i>
              Document Guidelines
            </h4>
            <ul className="text-blue-700 text-sm space-y-1">
              {admissionsData.documentGuidelines?.map((guideline, index) => (
                <li key={index}>• {guideline}</li>
              ))}
            </ul>
          </div>
        </div>

        <div className="bg-white p-4 sm:p-6 rounded-xl border border-border_gray">
          <h3 className="text-lg sm:text-xl font-bold text-crimson mb-3 sm:mb-4">Helplines & Support</h3>
          <div className="grid sm:grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
            <div className="bg-green-50 p-4 rounded-lg border border-green-200">
              <h4 className="font-semibold text-green-800 mb-2">Admission Helpline</h4>
              <p className="text-green-700 font-bold text-lg">{admissionsData.admissionHelpline}</p>
              <p className="text-green-600 text-sm">{admissionsData.admissionHelplineHours}</p>
            </div>
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
              <h4 className="font-semibold text-blue-800 mb-2">Email Support</h4>
              <p className="text-blue-700 font-bold">{admissionsData.emailSupport}</p>
              <p className="text-blue-600 text-sm">{admissionsData.emailSupportNote}</p>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const sections = [
    { id: 'process', label: 'Admission Process', component: renderProcessSection },
    { id: 'payment', label: 'Payment Procedure', component: renderPaymentSection },
    { id: 'documents', label: 'Document Checklist', component: renderDocumentsSection }
  ];

  if (loading) {
    return (
      <div className="h-full w-full fade-in flex items-center justify-center min-h-screen">
        <div className="text-center">
          <i className="fas fa-spinner fa-spin text-4xl text-crimson mb-4"></i>
          <p className="text-gray-600">Loading admission information...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="h-full w-full fade-in flex items-center justify-center min-h-screen">
        <div className="text-center">
          <i className="fas fa-exclamation-circle text-4xl text-red-500 mb-4"></i>
          <p className="text-gray-600">{error}</p>
          <button 
            onClick={fetchAdmissionsData}
            className="mt-4 px-4 py-2 bg-crimson text-white rounded-lg hover:bg-crimson/90 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full w-full  fade-in">
      <Banner 
        title={admissionsData?.bannerTitle || "Admissions"}
        subtitle={admissionsData?.bannerSubtitle || "Join Our Community | Your Journey to Excellence Starts Here"}
        imageSrc={admissionsData?.bannerImage ? (admissionsData.bannerImage.startsWith('http') ? admissionsData.bannerImage : getImageUrl(admissionsData.bannerImage)) : "https://images.unsplash.com/photo-1427504494785-3a9ca7044f45?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80"}
      />
      <div className="max-w-6xl mx-auto p-4 sm:p-6 md:p-10">
        
        {/* Admission Enquiry Button */}
        <div className="mb-8 text-center">
          <button
            onClick={() => setIsEnquiryFormOpen(true)}
            className="bg-crimson hover:bg-red-800 text-white font-bold py-4 px-8 rounded-lg transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-105 inline-flex items-center gap-3"
          >
            <i className="fas fa-file-alt text-xl"></i>
            <span className="text-lg">Admission Enquiry</span>
          </button>
          <p className="mt-3 text-gray-600 text-sm">
            Start your admission process by filling out our enquiry form
          </p>
        </div>

        {/* Section Navigation */}
        <div className="mb-6 sm:mb-8 border-b border-gray-200">
          <div className="flex space-x-4 sm:space-x-8 overflow-x-auto">
            {sections.map((section) => (
              <button
                key={section.id}
                onClick={() => setActiveSection(section.id)}
                className={`py-3 sm:py-4 px-2 border-b-2 font-semibold whitespace-nowrap transition-all ${
                  activeSection === section.id
                    ? 'border-crimson text-crimson'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                {section.label}
              </button>
            ))}
          </div>
        </div>

        {/* Active Section Content */}
        {sections.find(section => section.id === activeSection)?.component()}

      </div>

      {/* Admission Enquiry Form Modal */}
      <AdmissionEnquiryForm 
        isOpen={isEnquiryFormOpen} 
        onClose={() => setIsEnquiryFormOpen(false)} 
      />
    </div>
  );
};

export default AdmissionPage;